'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { assignmentRulesListMachine } from './machines/assignmentRulesListMachine'
import type { CrmQueueAssignmentRule } from '../../../entities/queue/model/routing-types'

type AssignmentRulesMachineApi = ReturnType<typeof useMachine<typeof assignmentRulesListMachine>>

const AssignmentRulesListMachineContext = createContext<AssignmentRulesMachineApi | null>(null)

export function AssignmentRulesListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(assignmentRulesListMachine)
  return (
    <AssignmentRulesListMachineContext.Provider value={api}>
      {children}
    </AssignmentRulesListMachineContext.Provider>
  )
}

export function useAssignmentRulesListMachineOptional() {
  return useContext(AssignmentRulesListMachineContext)
}

export function useAssignmentRulesListMachine() {
  const ctx = useContext(AssignmentRulesListMachineContext)
  if (!ctx) {
    throw new Error(
      'useAssignmentRulesListMachine must be used within AssignmentRulesListMachineProvider'
    )
  }
  return ctx
}

export function useAssignmentRulesListStoreCompat() {
  const machineApi = useAssignmentRulesListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const ctx = state.context
    return {
      rules: ctx.rules,
      isLoading: ctx.isLoading,
      error: ctx.error,
      setRules: (rules: CrmQueueAssignmentRule[]) => send({ type: 'SET_RULES', rules }),
      addRule: (rule: CrmQueueAssignmentRule) => send({ type: 'ADD_RULE', rule }),
      updateRule: (id: string, updates: Partial<CrmQueueAssignmentRule>) =>
        send({ type: 'UPDATE_RULE', id, updates }),
      deleteRule: (id: string) => send({ type: 'DELETE_RULE', id }),
      reorderRules: (ruleIds: string[]) => send({ type: 'REORDER_RULES', ruleIds }),
      toggleRule: (id: string) => send({ type: 'TOGGLE_RULE', id }),
      setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
      setError: (error: string | null) => send({ type: 'SET_ERROR', error }),
    }
  }, [machineApi])
}

export function useAssignmentRulesActions() {
  const machineApi = useAssignmentRulesListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setRules: (rules: CrmQueueAssignmentRule[]) => send({ type: 'SET_RULES', rules }),
    addRule: (rule: CrmQueueAssignmentRule) => send({ type: 'ADD_RULE', rule }),
    updateRule: (id: string, updates: Partial<CrmQueueAssignmentRule>) =>
      send({ type: 'UPDATE_RULE', id, updates }),
    deleteRule: (id: string) => send({ type: 'DELETE_RULE', id }),
    reorderRules: (ruleIds: string[]) => send({ type: 'REORDER_RULES', ruleIds }),
    toggleRule: (id: string) => send({ type: 'TOGGLE_RULE', id }),
    setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
    setError: (error: string | null) => send({ type: 'SET_ERROR', error }),
  }), [send])
}

export function useAssignmentRulesList() {
  const compat = useAssignmentRulesListStoreCompat()
  return compat.rules
}

export function useAssignmentRulesLoading() {
  const compat = useAssignmentRulesListStoreCompat()
  return compat.isLoading
}

export function useAssignmentRulesError() {
  const compat = useAssignmentRulesListStoreCompat()
  return compat.error
}
